import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Frontend service for staff message operations
 * Coordinates staff message sending and enrichment
 */
const functions = getFunctions();

/**
 * Send a message from a staff member
 * This ensures proper staff identity is stored for future lookups
 * @param contactName - Name of the conversation participant (for draft metadata)
 * @param contactAvatarUrl - Avatar of the participant (for draft metadata)
 */
export async function sendStaffMessage(
  conversationId: string,
  text: string,
  staffId: string,
  staffName?: string,
  channel?: 'CHAT' | 'GMAIL' | 'OUTLOOK' | 'YAHOO' | 'OTHER_EMAIL' | 'SMS',
  contactName?: string,
  contactAvatarUrl?: string,
  targetChannelId?: string,
  subject?: string,
  fromEmail?: 'scheduling' | 'billing' | 'support',
  /** The app's own id for this send: a retry can't send twice, and the thread matches the saved message to its pending one */
  clientMessageId?: string,
  /** Files already uploaded to Wix Media (uploadEmailAttachment) to send with an email */
  attachments?: Array<{ id: string; url: string; name: string; mimeType: string; size: number }>
) {
  const wixAdminCallable = httpsCallable(functions, 'wixAdmin');
  return wixAdminCallable({
    collection: 'staffMessage',
    method: 'sendMessage',
    database: 'rs-roles',
    params: {
      conversationId,
      text,
      staffId,
      staffName,
      channel,
      contactName,
      contactAvatarUrl,
      targetChannelId,
      subject,
      fromEmail,
      clientMessageId,
      attachments,
    },
  });
}

/**
 * Enrich messages with staff metadata when loading from Wix
 * This is called when scrolling up to load older messages
 */
export async function enrichMessagesWithStaffMetadata(
  conversationId: string,
  messages: Record<string, unknown>[]
) {
  const wixAdminCallable = httpsCallable(functions, 'wixAdmin');
  return wixAdminCallable({
    collection: 'staffMessage',
    method: 'enrichMessagesWithStaffMetadata',
    database: 'rs-roles',
    params: {
      conversationId,
      messages,
    },
  });
}

/**
 * Get staff metadata for a single message
 */
export async function getMessageStaffMeta(messageId: string) {
  const wixAdminCallable = httpsCallable(functions, 'wixAdmin');
  return wixAdminCallable({
    collection: 'staffMessage',
    method: 'getMessageStaffMeta',
    database: 'rs-roles',
    params: {
      messageId,
    },
  });
}

/**
 * Batch get staff metadata for multiple messages
 * OPTIMIZATION: Send all message IDs in ONE call instead of N separate calls
 * Reduces network latency dramatically (15 calls → 1 call)
 */
export async function batchGetMessageStaffMeta(messageIds: string[]) {
  const wixAdminCallable = httpsCallable(functions, 'wixAdmin');
  return wixAdminCallable({
    collection: 'staffMessage',
    method: 'batchGetMessageStaffMeta',
    database: 'rs-roles',
    params: {
      messageIds,
    },
  });
}
